import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useCallback, useState } from 'react';
import { Container } from 'components/Container';
import { DocumentTitle } from 'components/DocumentTitle';
import { useTrack } from 'utils/hooks/useMixPanel';
import { ScheduleMixpanelEvents } from 'domains/direct-schedule/mixpanel';
import { Heading, Paragraph } from 'components/Typography';
import { Card } from 'components/Card';
import { useNavigation } from 'utils/hooks/useNavigation';
import { Button } from 'components/Button';
import { useBreakpoint } from 'utils/hooks/useBreakpoint';
import { isTimeSlotAvailable, useScheduleState } from 'domains/direct-schedule/useScheduleProvider';
import { SchedulePendingScheduleDto, schedulePendingSchedule } from 'domains/direct-schedule/queries';
import { ApiError } from 'domains/api/errors';
import { useFlashMessages } from 'domains/flash-messages';
import { useModal } from 'utils/hooks/useModal';
import { AppointmentDate } from 'components/AppointmentDate';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { ScheduleConfirmationModal, ScheduleConfirmationResponse } from './ScheduleConfirmationModal';
import { FlashMessage } from 'components/FlashMessages';
import { Schedule } from 'domains/direct-schedule/types';

const ScheduleConfirmation = () => {
  useTrack(ScheduleMixpanelEvents.ViewedScheduleConfirmation);

  const { openModal, modalProps } = useModal({ dismissable: false });
  const { selectedTime, scheduleQuery, clientId, scheduleId, calendarId, therapistName } = useScheduleState();
  const queryClient = useQueryClient();

  const { displayTimezone } = useDisplayTimezone();
  const { routes, navigate: sendTo } = useNavigation();

  const [modalStatus, setModalStatus] = useState<ScheduleConfirmationResponse | null>(null);
  const [scheduleResponse, setScheduleResponse] = useState<Schedule | null>(null);

  const { t } = useTranslation();
  const { createFlash } = useFlashMessages();
  const navigate = useNavigate();

  const isMobile = useBreakpoint({
    max: 'xs',
  });

  const { mutateAsync: scheduleAppointment, isLoading: isRescheduling } = useMutation<
    unknown,
    ApiError,
    SchedulePendingScheduleDto
  >(
    data => {
      if (!clientId || !scheduleId || !calendarId) {
        throw new Error('clientId, scheduleId and calendarId is undefined');
      }

      const response = schedulePendingSchedule(clientId, scheduleId, data);
      return response;
    },
    {
      onSuccess: async (data: any) => {
        setModalStatus('success');
        setScheduleResponse(data);
        openModal();
      },
      onError: async error => {
        // @ts-expect-error @TODO proper types here
        const code = error?.originalError?.response?.data?.code;

        switch (code) {
          case 'PENDING_RESCHEDULE_NOT_ACTIVE':
          case 'ACUITY_CALENDAR_NOT_AVAILABILITY':
            await queryClient.invalidateQueries('schedule-available-times');
            setModalStatus('inactive');
            break;
          case 'PENDING_RESCHEDULE_NOT_FOUND':
            setModalStatus('notfound');
            break;
          default:
            // dev-note(gian): this is a catch-all error message for any other error that we don't know about
            sendTo(routes.root, {
              flash: createFlash('error', t('schedule.confirm.genericError')),
            });
            return;
        }

        openModal();
      },
    },
  );

  const onConfirm = useCallback(() => {
    if (modalStatus !== 'success') navigate(-1);
    else {
      if (!scheduleResponse || !scheduleResponse.invitationLink) {
        sendTo(routes.root);
      } else {
        try {
          const invitationLinkUrl = new URL(scheduleResponse.invitationLink);
          sendTo(`${invitationLinkUrl.pathname}${invitationLinkUrl.search}`);
        } catch (err: any) {
          console.error(err);
          sendTo(routes.root);
        }
      }
    }
  }, [modalStatus, scheduleResponse]);

  if (!selectedTime || !displayTimezone) {
    return null;
  }

  const onSubmit = () => {
    // edge-case: if the user has been on the page for more than 2 hours from the selected timeslot, we don't allow them to schedule
    if (!isTimeSlotAvailable(selectedTime)) {
      setModalStatus('inactive');
      openModal();

      return;
    }

    // edge-case: calendarId should always be filled after selecting time
    if (!calendarId) {
      return;
    }

    scheduleAppointment({
      calendarID: calendarId,
      dateTime: selectedTime.toISOString(),
      timeZone: displayTimezone?.resolvedName ?? '',
    });
  };

  return (
    <>
      <DocumentTitle>{t('schedule.pageTitle')}</DocumentTitle>
      <Container className="bg-cream-50  max-w-[51rem] in-h-full flex flex-col items-center" size="full">
        <div className="flex-col mb-40 md:mb-64 items-start">
          <Heading level="h2" className="mb-18">
            {t('schedule.confirm.mainHeading')}
          </Heading>
          <Paragraph className="mb-40 md:mb-64">{t('schedule.confirm.subTitle')}</Paragraph>
          <Card className="mb-64 bg-orchid-100">
            <Heading level="h6" className="mb-16">
              {t('schedule.confirm.therapistTitle')}
            </Heading>
            <Heading level="h5" className="mt-20 mb-6">
              {therapistName}
            </Heading>
            <Paragraph className="mb-16">M.S., CCC-SLP</Paragraph>
            <Paragraph className="mb-16">
              {t('schedule.confirm.therapistDescription', {
                name: therapistName,
                state: scheduleQuery.data?.state ?? '',
              })}
            </Paragraph>
          </Card>
          <Card>
            <Heading level="h6" className="mb-16">
              {t('schedule.confirm.dateTimeTitle')}
            </Heading>
            <div className="mt-40 mb-16 flex items-center justify-between">
              <Heading level="h5">{t('schedule.confirm.dateTimeSubTitle')}</Heading>
              <Button size="sm" onClick={() => navigate(-1)} variant="ghost">
                {t('schedule.confirm.edit')}
              </Button>
            </div>
            <AppointmentDate
              date={new Date(selectedTime)}
              dateSuffix={t('schedule.confirm.therapistAppointmentComplement', { name: therapistName })}
            />
          </Card>
        </div>

        <FlashMessage className="w-full mb-64 sm-d:mb-40" variant="warning">
          {t('schedule.confirm.scheduleAlert')}
        </FlashMessage>

        <div className="flex flex-col sm:flex-row w-full xs:w-auto">
          <Button
            onClick={onSubmit}
            size="lg"
            className="mb-[16px] sm:mb-0 sm:mr-8"
            fullWidth={isMobile}
            loading={isRescheduling}
            disabled={isRescheduling}
            variant="primary"
          >
            {t('schedule.confirm.submit')}
          </Button>
          <Button size="lg" onClick={() => navigate(-1)} variant="ghost">
            {t('schedule.confirm.cancel')}
          </Button>
        </div>

        <ScheduleConfirmationModal modalProps={modalProps} status={modalStatus} onConfirm={onConfirm} />
      </Container>
    </>
  );
};

export default ScheduleConfirmation;
